import type { ColumnDef } from '@tanstack/react-table';
import { get } from 'lodash-es';

import { getOriginalRowData } from '../helpers';
import type { RowSpan, RowSpanConfiguration, TableColumn, TanStackColumnMeta } from '../types';

export const getMergeByColumnKeys = <T>(columns: TableColumn<T>[]): string[] =>
  columns.filter((column) => column.mergeBy).map((column) => column.key);

/**
 * Adapts TableColumn configuration to TanStack ColumnDef format.
 * @param TSource - The original data type from TableColumn configuration
 * @param TRow - The runtime row type (typically DataWithRowId<TSource, K>)
 */
export const adaptColumnsToTanStack = <TSource extends object, TRow extends TSource = TSource>(
  columns: TableColumn<TSource>[],
  rowSpanConfiguration: RowSpanConfiguration,
  mergeByColumnKeys: string[]
): ColumnDef<TRow, unknown>[] => {
  return columns.map((column): ColumnDef<TRow, unknown> => {
    const { key, dataKey, render, sortKey, sortable, resizable, width, alignment, ellipsis, fixed, className, bulkSelection } =
      column;

    const isPartOfMergeByColumn = mergeByColumnKeys.includes(key);
    const rowSpans: RowSpan[] | undefined = rowSpanConfiguration[key];

    const isResizable = resizable ?? true;

    const meta: TanStackColumnMeta<TSource> = {
      alignment,
      className,
      columnKey: key,
      ellipsis: ellipsis ?? false,
      fixed,
      isPartOfMergeByColumn,
      resizable: isResizable,
      sortable: sortable ?? true,
      sortKey: sortKey ?? key,
      rowSpans,
      width,
      originalConfig: column,
      bulkSelection
    };

    return {
      id: key,
      accessorFn: (row: TRow) => get(row, dataKey || key),
      header: () => column.title,
      cell: (info) => {
        if (render) {
          const originalData = getOriginalRowData(info.row.original) as TSource;

          return render(info.getValue(), originalData, info.row.index);
        }

        return info.getValue() as React.ReactNode;
      },
      size: typeof width === 'number' ? width : undefined,
      enableResizing: isResizable,
      meta
    };
  });
};

export const shouldSkipCell = (rowSpans: RowSpan[] | undefined, rowIndex: number): boolean => {
  if (!rowSpans) return false;

  return rowSpans.some((span) => rowIndex > span.atIndex && rowIndex < span.atIndex + span.value);
};

export const getRowSpanValue = (rowSpans: RowSpan[] | undefined, rowIndex: number): number | undefined => {
  if (!rowSpans) return undefined;

  const rowSpan = rowSpans.find((span) => span.atIndex === rowIndex);

  return rowSpan?.value;
};
